import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Search, ChevronRight, User, Calendar, BookOpen, Play, FileText, Award, ChevronLeft, GraduationCap, Video, Filter, ChevronDown, Eye } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';

const SUBJECTS = [
  'Cas Cliniques', 'Entretien', 'Keratocone', 'Lentilles Hybrides',
  'Lentilles Colorees', 'Lentilles Sclerales', 'Lentilles Souples', 'LRPG',
  'Myopie', 'Organisation', 'Orthokeratologie', 'Pathologies',
  'Pediatrie', 'Presbytie', 'Événements'
];

// Display name with accents for UI
const SUBJECT_LABELS = {
  'Cas Cliniques': 'Cas Cliniques', 'Entretien': 'Entretien', 'Keratocone': 'Kératocône',
  'Lentilles Hybrides': 'Lentilles Hybrides', 'Lentilles Colorees': 'Lentilles Colorées',
  'Lentilles Sclerales': 'Lentilles Sclérales', 'Lentilles Souples': 'Lentilles Souples',
  'LRPG': 'LRPG', 'Myopie': 'Myopie', 'Organisation': 'Organisation',
  'Orthokeratologie': 'Orthokératologie', 'Pathologies': 'Pathologies',
  'Pediatrie': 'Pédiatrie', 'Presbytie': 'Presbytie', 'Événements': 'Événements',
};

const CATEGORY_STYLES = {
  'Les Rapports': { gradient: 'from-amber-500 to-orange-600', icon: FileText, label: 'Rapport' },
  'Les Publications': { gradient: 'from-emerald-500 to-teal-600', icon: BookOpen, label: 'Publication' },
  'Les Cours': { gradient: 'from-blue-500 to-indigo-600', icon: GraduationCap, label: 'Cours' },
  'Les Replays': { gradient: 'from-violet-500 to-purple-600', icon: Video, label: 'Replay' },
  'Grand Prix': { gradient: 'from-rose-500 to-pink-600', icon: Award, label: 'Grand Prix' },
  'Cahier': { gradient: 'from-cyan-500 to-sky-600', icon: BookOpen, label: 'Cahier' },
};

const ARTICLE_TYPES = [
  { label: 'Les rapports', value: 'Les Rapports', icon: FileText },
  { label: 'Les publications scientifiques', value: 'Les Publications', icon: BookOpen },
  { label: 'Les cours', value: 'Les Cours', icon: BookOpen },
  { label: 'Les replays', value: 'Les Replays', icon: Play },
  { label: 'Le grand prix de contactologie', value: 'Grand Prix', icon: Award },
];

const ITEMS_PER_PAGE = 12;

const MobileFilterBar = ({ selectedCategory, setSelectedCategory, selectedYear, setSelectedYear, selectedSubject, setSelectedSubject, selectedAuthor, setSelectedAuthor, years, authors, setCurrentPage }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');

  const selectFilter = (setter, value, current) => {
    setter(current === value ? '' : value);
    setCurrentPage(1);
    setOpen(false);
  };

  return (
    <div className="lg:hidden mb-4">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-[#d0e8f0] rounded-xl text-sm font-semibold text-[#35AFE4] hover:bg-[#eaf6fc] transition-colors"
        data-testid="mobile-filter-toggle">
        <span className="flex items-center gap-2"><Filter size={15} /> Filtrer les formations</span>
        <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-2 bg-white border border-[#d0e8f0] rounded-2xl overflow-hidden shadow-lg" data-testid="mobile-filter-panel">
          <div className="flex border-b border-gray-100 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {[
              { key: 'categories', label: 'Catégories' },
              { key: 'subjects', label: 'Sujets' },
              { key: 'years', label: 'Périodes' },
              { key: 'authors', label: 'Auteurs' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-shrink-0 px-4 py-2.5 text-xs font-semibold transition-colors ${activeTab === tab.key ? 'text-[#35AFE4] border-b-2 border-[#35AFE4]' : 'text-gray-400'}`}
                data-testid={`mobile-tab-${tab.key}`}>
                {tab.label}
              </button>
            ))}
          </div>
          <div className="p-4 max-h-[300px] overflow-y-auto">
            {activeTab === 'categories' && ARTICLE_TYPES.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.label} onClick={() => selectFilter(setSelectedCategory, t.value, selectedCategory)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${selectedCategory === t.value ? 'bg-[#eaf6fc] text-[#35AFE4] font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>
                  <Icon size={14} className="text-[#35AFE4]/60 flex-shrink-0" /> {t.label}
                </button>
              );
            })}
            {activeTab === 'subjects' && SUBJECTS.map(s => {
              return (
                <button key={s} onClick={() => selectFilter(setSelectedSubject, s, selectedSubject)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${selectedSubject === s ? 'bg-[#eaf6fc] text-[#35AFE4] font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>
                  <FileText size={12} className="text-[#35AFE4]/60 flex-shrink-0" />
                  <span className="flex-1">{SUBJECT_LABELS[s] || s}</span>
                </button>
              );
            })}
            {activeTab === 'years' && (
              <div className="flex flex-wrap gap-2">
                {years.map(y => (
                  <button key={y} onClick={() => selectFilter(setSelectedYear, y, selectedYear)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${selectedYear === y ? 'bg-[#35AFE4] text-white' : 'text-[#35AFE4] hover:bg-[#eaf6fc]'}`}>
                    {y}
                  </button>
                ))}
              </div>
            )}
            {activeTab === 'authors' && authors.map(a => (
              <button key={a} onClick={() => selectFilter(setSelectedAuthor, a, selectedAuthor)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${selectedAuthor === a ? 'text-[#35AFE4] font-bold' : 'text-gray-600 hover:text-[#35AFE4]'}`}>
                {a}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PublicFormationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formations, setFormations] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Read initial filters from URL params (and infer from pathname)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('cat');
    const year = params.get('year');
    const subject = params.get('subject');
    const author = params.get('author');
    // Path-based defaults — keeps a single component for /nos-formations /publications /nos-replays
    if (!cat) {
      if (location.pathname.startsWith('/publications')) setSelectedCategory('Les Publications');
      else if (location.pathname.startsWith('/nos-replays')) setSelectedCategory('Les Replays');
    } else {
      setSelectedCategory(cat);
    }
    if (year) setSelectedYear(year);
    if (subject) setSelectedSubject(subject);
    if (author) setSelectedAuthor(author);
  }, [location.search, location.pathname]);

  useEffect(() => {
    axios.get(`${API}/formations/public`).then(r => setFormations(r.data)).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/ad-banners?placement=formations_top&status=active`).then(r => setTopBanners(r.data || [])).catch(() => {});
  }, []);

  // Banner auto-rotate
  useEffect(() => {
    if (topBanners.length <= 1) return;
    const timer = setInterval(() => setBannerIndex(prev => (prev + 1) % topBanners.length), 6000);
    return () => clearInterval(timer);
  }, [topBanners.length]);

  const years = [...new Set(formations.map(f => f.year).filter(Boolean))].sort((a, b) => b - a);
  const authors = [...new Set(formations.map(f => f.instructor).filter(Boolean))].sort();

  const filtered = formations.filter(f => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!f.title.toLowerCase().includes(q) && !(f.description || '').toLowerCase().includes(q) && !(f.instructor || '').toLowerCase().includes(q)) return false;
    }
    if (selectedCategory && !f.category?.includes(selectedCategory)) return false;
    if (selectedYear && f.year !== selectedYear) return false;
    // Subject can be the category OR a tag (formations may have category="Les Cours"
    // and tags=["Myopie", ...], so we match both fields).
    if (selectedSubject) {
      const inCategory = f.category === selectedSubject;
      const inTags = Array.isArray(f.tags) && f.tags.includes(selectedSubject);
      if (!inCategory && !inTags) return false;
    }
    if (selectedAuthor && f.instructor !== selectedAuthor) return false;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const clearFilters = () => {
    setSelectedCategory(''); setSelectedYear(''); setSelectedSubject(''); setSelectedAuthor(''); setSearchQuery(''); setCurrentPage(1);
  };

  const hasFilters = selectedCategory || selectedYear || selectedSubject || selectedAuthor || searchQuery;

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Roboto', sans-serif", background: 'linear-gradient(180deg, #e8f4f8 0%, #f0f8fb 50%, #f8fcfd 100%)' }} data-testid="formations-page">
      <SEO title="Formation" description="Articles de formation en contactologie. Cours, replays, publications scientifiques et rapports de la SFOALC." />
      <SiteHeader />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Banner carousel above search */}
        {topBanners.length > 0 && (() => {
          const b = topBanners[bannerIndex];
          const baseUrl = API.replace('/api', '');
          const imgSrc = b.image_url?.startsWith('/') ? `${baseUrl}${b.image_url}` : b.image_url;
          return (
            <div className="mb-6 relative rounded-2xl overflow-hidden shadow-sm max-w-2xl mx-auto" data-testid="formations-banner">
              <a href={b.link_url || '#'} target="_blank" rel="noopener noreferrer"
                onClick={() => { if (b.id) axios.post(`${API}/ad-banners/${b.id}/click`).catch(() => {}); }}>
                <img src={imgSrc} alt={b.title || ''} className="w-full h-auto" style={{ aspectRatio: '4 / 1', objectFit: 'cover' }} />
              </a>
              {topBanners.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {topBanners.map((_, i) => (
                    <button key={i} onClick={() => setBannerIndex(i)}
                      className={`w-2 h-2 rounded-full transition-all ${i === bannerIndex ? 'bg-white w-5' : 'bg-white/50'}`} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        <div className="flex gap-8">
          {/* ===== SIDEBAR ===== */}
          <aside className="w-64 flex-shrink-0 hidden lg:block" data-testid="sidebar-filters">
            <div className="sticky top-20 space-y-4">

              {/* Périodes */}
              {years.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#d0e8f0] p-5">
                  <h3 className="text-base font-bold text-[#35AFE4] mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Les périodes</h3>
                  <div className="flex flex-wrap gap-2">
                    {years.map(y => (
                      <button key={y} onClick={() => { setSelectedYear(selectedYear === y ? '' : y); setCurrentPage(1); }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${selectedYear === y ? 'bg-[#35AFE4] text-white' : 'text-[#35AFE4] hover:bg-[#eaf6fc]'}`}
                        data-testid={`filter-year-${y}`}
                      >
                        <Calendar size={11} /> {y}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Sujets */}
              <div className="bg-white rounded-2xl border border-[#d0e8f0] p-5">
                <h3 className="text-base font-bold text-[#35AFE4] mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Les sujets</h3>
                <ul className="space-y-0.5">
                  {SUBJECTS.map(s => {
                    const count = formations.filter(f => f.category === s || (Array.isArray(f.tags) && f.tags.includes(s))).length;
                    return (
                      <li key={s}>
                        <button onClick={() => { setSelectedSubject(selectedSubject === s ? '' : s); setCurrentPage(1); }}
                          className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-2 ${selectedSubject === s ? 'bg-[#eaf6fc] text-[#35AFE4] font-bold' : 'text-gray-600 hover:bg-gray-50 hover:text-[#35AFE4]'}`}
                          data-testid={`filter-subject-${s}`}
                        >
                          <FileText size={11} className="text-[#35AFE4]/60 flex-shrink-0" />
                          <span className="flex-1">{SUBJECT_LABELS[s] || s}</span>
                          {count > 0 && <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{count}</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Articles de formation */}
              <div className="bg-white rounded-2xl border border-[#d0e8f0] p-5">
                <h3 className="text-base font-bold text-[#35AFE4] mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Les articles de formation</h3>
                <ul className="space-y-0.5">
                  {ARTICLE_TYPES.map(t => {
                    const Icon = t.icon;
                    const catValue = t.value || t.label;
                    const count = formations.filter(f => f.category && f.category.includes(catValue)).length;
                    return (
                      <li key={t.label}>
                        <button onClick={() => { setSelectedCategory(selectedCategory === catValue ? '' : catValue); setCurrentPage(1); }}
                          className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-2 ${selectedCategory === catValue ? 'bg-[#eaf6fc] text-[#35AFE4] font-bold' : 'text-gray-600 hover:bg-gray-50 hover:text-[#35AFE4]'}`}
                          data-testid={`filter-cat-${t.label}`}
                        >
                          <Icon size={12} className="text-[#35AFE4]/60 flex-shrink-0" />
                          <span className="flex-1">{t.label}</span>
                          {count > 0 && <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{count}</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Auteurs */}
              {authors.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#d0e8f0] p-5">
                  <h3 className="text-base font-bold text-[#35AFE4] mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Les auteurs</h3>
                  <div className="space-y-0.5 max-h-[250px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                    {selectedAuthor && (
                      <button onClick={() => { setSelectedAuthor(''); setCurrentPage(1); }}
                        className="w-full text-left px-3 py-1 text-xs font-semibold text-[#35AFE4] hover:underline mb-1">
                        Tous les auteurs
                      </button>
                    )}
                    {authors.map(a => (
                      <button key={a} onClick={() => { setSelectedAuthor(selectedAuthor === a ? '' : a); setCurrentPage(1); }}
                        className={`w-full text-left px-3 py-1 text-xs transition-colors ${selectedAuthor === a ? 'text-[#35AFE4] font-bold' : 'text-gray-500 hover:text-[#35AFE4]'}`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>

          {/* ===== MAIN CONTENT ===== */}
          <main className="flex-1 min-w-0">
            {/* Mobile filter bar */}
            <MobileFilterBar
              selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
              selectedYear={selectedYear} setSelectedYear={setSelectedYear}
              selectedSubject={selectedSubject} setSelectedSubject={setSelectedSubject}
              selectedAuthor={selectedAuthor} setSelectedAuthor={setSelectedAuthor}
              years={years} authors={authors} setCurrentPage={setCurrentPage}
            />
            {/* Search bar */}
            <div className="flex items-center gap-3 mb-6">
              <div className="relative flex-1">
                <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  placeholder="Que recherchez-vous ?"
                  className="w-full px-5 py-3.5 bg-white border border-[#d0e8f0] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4] transition-all pr-12"
                  data-testid="search-input"
                />
              </div>
              <button className="w-12 h-12 rounded-xl bg-[#35AFE4] text-white flex items-center justify-center hover:bg-[#2d7fa5] transition-colors shadow-sm" data-testid="search-btn">
                <Search size={18} />
              </button>
            </div>

            {/* Active filters */}
            {hasFilters && (
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-xs text-gray-500">Filtres :</span>
                {selectedCategory && <span className="px-2.5 py-1 bg-[#eaf6fc] text-[#35AFE4] text-[11px] font-bold rounded-full">{ARTICLE_TYPES.find(t => t.value === selectedCategory)?.label || selectedCategory} <button onClick={() => setSelectedCategory('')}>&times;</button></span>}
                {selectedYear && <span className="px-2.5 py-1 bg-[#eaf6fc] text-[#35AFE4] text-[11px] font-bold rounded-full">{selectedYear} <button onClick={() => setSelectedYear('')}>&times;</button></span>}
                {selectedSubject && <span className="px-2.5 py-1 bg-[#eaf6fc] text-[#35AFE4] text-[11px] font-bold rounded-full">{selectedSubject} <button onClick={() => setSelectedSubject('')}>&times;</button></span>}
                {selectedAuthor && <span className="px-2.5 py-1 bg-[#eaf6fc] text-[#35AFE4] text-[11px] font-bold rounded-full">{selectedAuthor} <button onClick={() => setSelectedAuthor('')}>&times;</button></span>}
                <button onClick={clearFilters} className="text-xs text-red-400 hover:text-red-600 underline">Tout effacer</button>
              </div>
            )}

            {/* Results */}
            {loading ? (
              <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#35AFE4]" /></div>
            ) : paginated.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-[#d0e8f0]">
                <BookOpen size={48} className="mx-auto mb-4 text-[#35AFE4]/30" />
                <h3 className="text-lg font-bold text-gray-700 mb-2">Aucune formation trouvée</h3>
                <p className="text-gray-500 text-sm">Essayez de modifier vos filtres</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="formations-grid">
                  {paginated.map((f, i) => {
                    const catStyle = CATEGORY_STYLES[f.category] || { gradient: 'from-gray-400 to-gray-500', icon: FileText, label: f.category || 'Article' };
                    const CatIcon = catStyle.icon;
                    const hasThumbnail = f.thumbnail && f.thumbnail !== '';
                    return (
                    <div key={f.id}
                      onClick={() => navigate(`/nos-formations/${f.id}`)}
                      className="group bg-white rounded-2xl overflow-hidden border border-[#d0e8f0] cursor-pointer hover:shadow-xl hover:border-[#35AFE4]/40 hover:-translate-y-1 transition-all duration-300"
                      data-testid={`formation-card-${i}`}
                    >
                      {/* Image / Category Thumbnail */}
                      <div className="relative aspect-[16/10] overflow-hidden">
                        {hasThumbnail ? (
                          <img
                            src={f.thumbnail}
                            alt={f.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-gradient-to-br', ...catStyle.gradient.split(' ')); }}
                          />
                        ) : (
                          <div className={`w-full h-full bg-gradient-to-br ${catStyle.gradient} flex flex-col items-center justify-center transition-transform duration-500 group-hover:scale-105`}>
                            <CatIcon size={40} className="text-white/40 mb-2" strokeWidth={1.5} />
                            <span className="text-white/60 text-xs font-bold tracking-widest uppercase">{catStyle.label}</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        {f.pdf_url && (
                          <span className="absolute top-2 right-2 px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded-md shadow-sm" data-testid={`pdf-badge-${i}`}>PDF</span>
                        )}
                        {(f.video_url || (f.episodes && f.episodes.length > 0)) && (
                          <span className="absolute top-2 left-2 px-2 py-0.5 bg-violet-600 text-white text-[10px] font-bold rounded-md shadow-sm flex items-center gap-1">
                            <Play size={9} fill="white" /> VIDEO
                          </span>
                        )}
                        {/* Category badge bottom-left */}
                        <span className={`absolute bottom-2 left-2 px-2.5 py-1 bg-white/90 backdrop-blur-sm text-[10px] font-bold rounded-full shadow-sm`}
                          style={{ color: catStyle.gradient.includes('amber') ? '#d97706' : catStyle.gradient.includes('emerald') ? '#059669' : catStyle.gradient.includes('blue') ? '#2563eb' : catStyle.gradient.includes('violet') ? '#7c3aed' : catStyle.gradient.includes('rose') ? '#e11d48' : '#0891b2' }}
                        >
                          {catStyle.label}
                        </span>
                      </div>
                      {/* Content */}
                      <div className="p-4">
                        <h3 className="font-bold text-gray-900 text-sm mb-2 line-clamp-2 min-h-[2.5rem] group-hover:text-[#35AFE4] transition-colors" style={{ fontFamily: "'Barlow', sans-serif" }}>
                          {f.title}
                        </h3>
                        {/* Author row */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${catStyle.gradient} flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0`}>
                            {(f.instructor || 'S')[0]}
                          </div>
                          <div className="flex items-center gap-1 text-[11px] text-gray-500 min-w-0">
                            <span className="font-medium truncate">{f.instructor || 'SFOALC'}</span>
                            {f.year && <><span className="text-gray-300">·</span><span>{f.year}</span></>}
                            <span className="text-gray-300">·</span>
                            <span className="flex items-center gap-0.5"><Eye size={10} /> {f.views || 0}</span>
                          </div>
                        </div>
                        {/* Tags */}
                        {f.tags && f.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {f.tags.slice(0, 3).map((tag, ti) => (
                              <span key={ti} className="text-[10px] px-2 py-0.5 bg-[#eaf6fc] text-[#35AFE4] rounded-full font-medium">{tag}</span>
                            ))}
                          </div>
                        )}
                        {/* Découvrir button */}
                        <span
                          className="inline-flex items-center gap-1.5 px-5 py-2 bg-[#35AFE4] text-white text-xs font-bold rounded-full group-hover:bg-[#1e3a5f] transition-all duration-300 group-hover:shadow-md"
                          data-testid={`discover-btn-${i}`}
                        >
                          Découvrir <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-1 mt-8" data-testid="pagination">
                    <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
                      className="px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-white disabled:opacity-30 transition-colors">
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <button key={p} onClick={() => setCurrentPage(p)}
                        className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${currentPage === p ? 'bg-[#35AFE4] text-white shadow-sm' : 'text-gray-500 hover:bg-white'}`}
                        data-testid={`page-${p}`}
                      >
                        {p}
                      </button>
                    ))}
                    <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}
                      className="px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-white disabled:opacity-30 transition-colors">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PublicFormationsPage;
